import React, { useState, useEffect, useMemo } from 'react';
import { X, Mail, Plus, Trash2, Send, FileText } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

declare const process: {
  env: {
    REACT_APP_API_URL?: string;
  };
};

interface BookingRow {
  groupName: string;
  fileName: string;
  checkIn: string;
  checkOut: string;
  nights: number;
  rooms: number;
  status?: string;
  isEdited?: boolean;
}

interface ExtensionRow {
  passengerName: string;
  position?: 'pre' | 'post';
  date: string;
  sector: string;
  flightNumber: string;
  time: string;
  status: string;
  guideName: string;
  guideType: string;
  city: string;
  hotelName: string;
  hotelStatus: string;
  roomCategory: string;
  meals: string[];
  breakfastRemark: string;
  lunchRemark: string;
  dinnerRemark: string;
}

interface EmailAttachment {
  filename: string;
  mimeType: string;
  contentBase64: string;
}

interface SendToHotelModalProps {
  isOpen: boolean;
  onClose: () => void;
  hotelName: string;
  bookings: BookingRow[];
  hotelId?: string;
  mode?: 'hotels' | 'vouchers'; // New prop for mode
  groupName?: string; // New prop for group name when clicking from table
  tourFileData?: any; // Tour file data passed directly from BookingsPage
  selectedHotelName?: string;
  extensions?: ExtensionRow[];
  attachments?: EmailAttachment[];
}

export function SendToHotelModal({ 
  isOpen, 
  onClose, 
  hotelName, 
  bookings: initialBookings, 
  hotelId,
  mode = 'hotels',
  groupName,
  tourFileData: passedTourFileData,
  selectedHotelName,
  extensions: passedExtensions,
  attachments = []
}: SendToHotelModalProps) {
  const parseBookingDateValue = (dateStr: string) => {
    const normalized = formatDateForInput(dateStr || '');
    if (!normalized) return Number.POSITIVE_INFINITY;
    const parsed = new Date(normalized).getTime();
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  };

  const orderBookingsForMail = (rows: BookingRow[]) => {
    const sorted = [...rows].sort((a, b) => parseBookingDateValue(a.checkIn) - parseBookingDateValue(b.checkIn));
    const grouped = new Map<string, BookingRow[]>();
    sorted.forEach((row) => {
      const key = row.groupName || 'Other';
      if (!grouped.has(key)) grouped.set(key, []);
      grouped.get(key)!.push(row);
    });
    return Array.from(grouped.values()).flat();
  };

  const [bookings, setBookings] = useState<BookingRow[]>(initialBookings);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderEmail, setSenderEmail] = useState('operations@immerseindiatours.com');
  const [subject, setSubject] = useState(`${hotelName} - Booking Request - Series 2026`);
  const [message, setMessage] = useState(
    mode === 'vouchers' 
      ? '' // Empty for vouchers 
      : `Dear Team,\n\nGreetings from Immerse India Tours!\n\nI am pleased to share the dates for our upcoming series for 2026.\n\nI would appreciate it if you could provide the following details:\n  1. Rates for single, double, and extra beds for the specified dates.\n  2. Tour leader discounted rate when the group size is less than 8 pax. For groups of 8-12 pax, a 50% discount on the agreed rate, and for 12 pax and above, one single room complimentary.\n  3. A 15-day credit facility from the receipt of bills.\n  4. Availability on the mentioned dates.\n  5. Please share your MSME certificate if you have.`
  );
  const [closing, setClosing] = useState('Please let us know if you need any additional information. We look forward to hearing from you soon.');
  const [senderName, setSenderName] = useState('Operations Team');
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [sending, setSending] = useState(false);
  const [tourFileData, setTourFileData] = useState<any>(passedTourFileData || null);
  const [hotelRequirementText, setHotelRequirementText] = useState('');
  const [requirementPdfAttachment, setRequirementPdfAttachment] = useState<EmailAttachment | null>(null);
  const hasPassengerAttachments = attachments.length > 0;

  // Use passed tour file data or fetch if not provided
  useEffect(() => {
    if (passedTourFileData) {
      setTourFileData(passedTourFileData);
      return;
    }
    const fetchTourFileData = async () => {
      if (mode === 'vouchers' && groupName && isOpen) {
        try {
          const token = localStorage.getItem('authToken');
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
          const response = await fetch(`${API_URL}/tourfile/search?groupName=${encodeURIComponent(groupName)}`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          if (data.success && data.data?.length > 0) {
            setTourFileData(data.data[0]);
          }
        } catch (error) {
          console.error('Error fetching tour file data:', error);
        }
      }
    };
    fetchTourFileData();
  }, [mode, groupName, isOpen, passedTourFileData]);

  useEffect(() => {
    setBookings(orderBookingsForMail(initialBookings));
  }, [initialBookings]);

  useEffect(() => {
    if (isOpen) {
      checkGoogleConnection();
    }
  }, [isOpen]);

  const checkGoogleConnection = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/hotel-emails/auth/google/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setIsGoogleConnected(data.data?.connected && !data.data?.requiresReauth);
    } catch (error) {
      console.error('Error checking Google connection:', error);
      setIsGoogleConnected(false);
    }
  };

  const connectGoogleAccount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_URL}/hotel-emails/auth/google/url`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      
      if (data.success && data.data?.authUrl) {
        window.open(data.data.authUrl, '_blank', 'width=600,height=700');
        
        // Poll for connection status
        const interval = setInterval(async () => {
          await checkGoogleConnection();
          if (isGoogleConnected) {
            clearInterval(interval);
          }
        }, 2000);
      }
    } catch (error) {
      console.error('Error getting Google auth URL:', error);
    }
  };

  const handleBookingEdit = (index: number, field: keyof BookingRow, value: any) => {
    const updatedBookings = [...bookings];
    updatedBookings[index] = {
      ...updatedBookings[index],
      [field]: value,
      isEdited: true
    };
    setBookings(updatedBookings);
  };

  const addNewRow = () => {
    setBookings([
      ...bookings,
      {
        groupName: '',
        fileName: '',
        checkIn: '',
        checkOut: '',
        nights: 1,
        rooms: 12,
        status: '',
        isEdited: true
      }
    ]);
  };

  const removeRow = (index: number) => {
    setBookings(bookings.filter((_, i) => i !== index));
  };

  const formatDateForInput = (dateStr: string) => {
    // Convert supported display formats to "YYYY-MM-DD"
    if (!dateStr) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr.trim())) return dateStr.trim();

    const slashOrDash = dateStr.trim().match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})$/);
    if (slashOrDash) {
      const day = slashOrDash[1].padStart(2, '0');
      const month = slashOrDash[2].padStart(2, '0');
      return `${slashOrDash[3]}-${month}-${day}`;
    }

    const parts = dateStr.trim().split('-');
    if (parts.length !== 3) return '';
    
    const monthMap: {[key: string]: string} = {
      'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
      'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
      'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
    };
    
    const day = parts[0].padStart(2, '0');
    const month = monthMap[parts[1]] || '01';
    const year = '20' + parts[2];
    
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (isoDate: string) => {
    // Convert "2027-11-16" to "16-Nov-27"
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = String(date.getFullYear()).slice(-2);
    return `${day}-${month}-${year}`;
  };

  const formatDateForVoucherLine = (dateValue: string) => {
    const normalized = formatDateForInput(dateValue);
    if (!normalized) return dateValue || '';
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return dateValue || '';
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(date.getDate()).padStart(2, '0')} ${monthNames[date.getMonth()]} ${String(date.getFullYear()).slice(-2)}`;
  };

  const parseTourItineraryRows = () => {
    try {
      const raw = typeof tourFileData?.itinerary === 'string'
        ? JSON.parse(tourFileData.itinerary)
        : (tourFileData?.itinerary || []);
      return Array.isArray(raw) ? raw : [];
    } catch {
      return [];
    }
  };

  const getItineraryRowsForDate = (dateValue: string) => {
    const target = formatDateForInput(dateValue);
    if (!target) return [];
    return parseTourItineraryRows().filter((row: any) => formatDateForInput(row?.date || '') === target);
  };

  const getHotelItineraryRowsForBooking = (booking: BookingRow) => {
    const hotelFilter = String(selectedHotelName || hotelName || '').trim().toLowerCase();
    const checkInValue = parseBookingDateValue(booking.checkIn);
    const checkOutValue = parseBookingDateValue(booking.checkOut);
    return parseTourItineraryRows()
      .filter((row: any) => {
        const rowDateValue = parseBookingDateValue(row?.date || '');
        const rowHotel = String(row?.hotelName || row?.hotel || '').trim().toLowerCase();
        return (
          rowHotel === hotelFilter &&
          rowDateValue >= checkInValue &&
          rowDateValue < checkOutValue
        );
      })
      .sort((a: any, b: any) => parseBookingDateValue(a.date) - parseBookingDateValue(b.date));
  };

  const mealNamesFromRows = (rows: any[]) => {
    const seen = new Set<string>();
    rows.forEach((row) => {
      const meals = Array.isArray(row?.meals) ? row.meals : [];
      meals.forEach((meal: any) => {
        const clean = String(meal || '').trim();
        if (clean) seen.add(clean);
      });
    });
    return Array.from(seen);
  };

  const buildActionText = (meals: string[], actions: string[]) => {
    const parts = [...meals, ...actions].filter(Boolean);
    return parts.join(' + ');
  };

  const addDaysToIsoDate = (isoDate: string, days: number) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return '';
    date.setDate(date.getDate() + days);
    return date.toISOString().slice(0, 10);
  };

  const getMealNamesForDate = (dateValue: string, fallbackBreakfast = true) => {
    const rows = getItineraryRowsForDate(dateValue);
    const meals = mealNamesFromRows(rows);
    return meals.length || !fallbackBreakfast ? meals : ['Breakfast'];
  };

  const ordinal = (value: number) => {
    const suffix = value % 10 === 1 && value % 100 !== 11 ? 'st'
      : value % 10 === 2 && value % 100 !== 12 ? 'nd'
      : value % 10 === 3 && value % 100 !== 13 ? 'rd'
      : 'th';
    return `${value}${suffix}`;
  };

  const getRoomRequestText = () => {
    if (!tourFileData) return '';
    const parts = [
      { label: 'Single', value: Number(tourFileData.accomSingle || 0) },
      { label: 'Double', value: Number(tourFileData.accomDouble || 0) },
      { label: 'Twin', value: Number(tourFileData.accomTwin || 0) },
      { label: 'Triple', value: Number(tourFileData.accomTriple || 0) }
    ].filter((item) => item.value > 0);
    if (!parts.length) return '';
    const totalRooms = parts.reduce((sum, item) => sum + item.value, 0);
    return `Please provide ${parts.map((item) => `${String(item.value).padStart(2, '0')} ${item.label}`).join(', ')} (Total ${String(totalRooms).padStart(2, '0')} Rooms) as per followings`;
  };

  const buildVisitLines = () => {
    return orderBookingsForMail(bookings).flatMap((booking, index) => {
      const lines = [`${ordinal(index + 1)} Visit`];
      const checkInIso = formatDateForInput(booking.checkIn);
      const nights = Math.max(1, Number(booking.nights || 1));

      for (let offset = 0; offset < nights; offset += 1) {
        const dateIso = addDaysToIsoDate(checkInIso, offset);
        const meals = getMealNamesForDate(dateIso, true);
        const actions = offset === 0 ? ['Check-in', 'Room'] : ['Room'];
        lines.push(`${formatDateForVoucherLine(dateIso)} : ${buildActionText(meals, actions)}`);
      }

      const checkOutMeals = getMealNamesForDate(booking.checkOut, true);
      lines.push(`${formatDateForVoucherLine(booking.checkOut)} : ${buildActionText(checkOutMeals, ['Check-out'])}`);
      return lines;
    });
  };

  const buildExtensionRequirementLines = () => {
    const rows = resolveExtensions() || [];
    if (!rows.length) return [];

    const normalizePosition = (position?: string) => position === 'pre' ? 'Pre' : 'Post';
    const hotelFilter = String(selectedHotelName || hotelName || '').trim().toLowerCase();
    const matchingRows = rows
      .filter((row: any) => {
        const rowHotel = String(row?.hotelName || row?.hotel || '').trim().toLowerCase();
        return !hotelFilter || rowHotel === hotelFilter || rowHotel.includes(hotelFilter) || hotelFilter.includes(rowHotel);
      })
      .sort((a: any, b: any) => parseBookingDateValue(a.date) - parseBookingDateValue(b.date));

    const grouped = matchingRows.reduce((acc: Record<string, any[]>, row: any) => {
      const key = `${row.position || 'post'}|${row.passengerName || ''}|${row.roomCategory || ''}`;
      if (!acc[key]) acc[key] = [];
      acc[key].push(row);
      return acc;
    }, {});

    return Object.values(grouped).flatMap((groupRows: any[]) => {
      const first = groupRows[0] || {};
      const position = normalizePosition(first.position);
      const room = String(first.roomCategory || 'Room').trim();
      const roomText = room.toLowerCase().includes('room') ? room : `${room} Room`;
      const sortedRows = [...groupRows].sort((a, b) => parseBookingDateValue(a.date) - parseBookingDateValue(b.date));
      const start = sortedRows[0];
      const last = sortedRows[sortedRows.length - 1];
      const endDate = getNextDayDisplay(last.date);
      const endActions = position === 'Pre'
        ? buildActionText(['Breakfast'], [])
        : buildActionText(['Breakfast'], ['Check-out']);

      return [
        `${position} ${groupRows.length} ${roomText}`,
        `${formatDateForVoucherLine(start.date)} : Check-in + Room`,
        `${formatDateForVoucherLine(endDate)} : ${endActions}`
      ];
    });
  };

  const buildHotelRequirementText = () => {
    const lines: string[] = [];
    const extensionsText = buildExtensionRequirementLines();
    if (extensionsText.length) {
      lines.push(...extensionsText, '');
    }

    const roomRequest = getRoomRequestText();
    if (roomRequest) {
      lines.push(roomRequest, '');
    }

    lines.push(...buildVisitLines());
    return lines.filter((line, index, arr) => line !== '' || arr[index - 1] !== '').join('\n');
  };

  const escapePdfText = (value: string) =>
    value
      .replace(/[^\x09\x0A\x0D\x20-\x7E]/g, '')
      .replace(/\\/g, '\\\\')
      .replace(/\(/g, '\\(')
      .replace(/\)/g, '\\)');

  const wrapPdfLine = (line: string, maxChars = 92) => {
    const words = line.split(/\s+/);
    const lines: string[] = [];
    let current = '';
    words.forEach((word) => {
      if (!current) {
        current = word;
      } else if ((current + ' ' + word).length <= maxChars) {
        current += ' ' + word;
      } else {
        lines.push(current);
        current = word;
      }
    });
    if (current || !words.length) lines.push(current);
    return lines;
  };

  const loadLogoForPdf = async () => {
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = '/logo.png';
      });
      const targetWidth = 116;
      const targetHeight = Math.max(42, Math.round((image.height / image.width) * targetWidth));
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
      return {
        data: atob(canvas.toDataURL('image/jpeg', 0.92).split(',')[1]),
        width: targetWidth,
        height: targetHeight
      };
    } catch {
      return null;
    }
  };

  const getSeriesCodeFromTourName = () => {
    const explicitCode = String(tourFileData?.seriesCode || '').trim();
    if (explicitCode) return explicitCode;

    const tourName = String(tourFileData?.tourName || tourFileData?.fileName || '').trim();
    const match = tourName.match(/\(([A-Z0-9]+)\)\s*$/i);
    return String(match?.[1] || '').trim();
  };

  const getSpecificGroupName = () => {
    const existingGroupName = String(groupName || bookings[0]?.groupName || '').trim();
    const fileCode = String(tourFileData?.fileCode || '').trim();

    if (existingGroupName && existingGroupName !== fileCode) {
      return existingGroupName;
    }

    const seriesCode = getSeriesCodeFromTourName();
    const startDateValue = tourFileData?.startDate || bookings[0]?.checkIn || '';
    const normalizedDate = formatDateForInput(startDateValue);
    const startDate = normalizedDate ? new Date(normalizedDate) : null;

    if (!seriesCode || !startDate || Number.isNaN(startDate.getTime())) {
      return existingGroupName || fileCode;
    }

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = String(startDate.getDate()).padStart(2, '0');
    const month = monthNames[startDate.getMonth()];
    const year = String(startDate.getFullYear()).slice(-2);
    return `${seriesCode}${day}${month}${year}`;
  };

  const buildRequirementPdfBase64 = async (contentText: string) => {
    const logo = await loadLogoForPdf();
    const groupNameValue = getSpecificGroupName();
    const fileCodeValue = tourFileData?.fileCode || bookings[0]?.groupName || '';
    const tourNameValue = tourFileData?.tourName || tourFileData?.fileName || '';
    const dateOfIssue = new Date().toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
    const paxText = `${String(tourFileData?.pax || bookings[0]?.rooms || '').padStart(2, '0')} Pax`;
    const agentText = tourFileData?.foreignTourOperator || '';

    const commands: string[] = [
      '0 0 1 RG',
      '1 w',
      '36 36 523 756 re S',
      '0 0 0 rg'
    ];

    if (logo) {
      commands.push('q');
      commands.push(`${logo.width} 0 0 ${logo.height} 54 702 cm`);
      commands.push('/Im1 Do');
      commands.push('Q');
    }

    const drawText = (text: string, x: number, y: number, size = 10, font = 'F1') => {
      commands.push(`BT /${font} ${size} Tf ${x} ${y} Td (${escapePdfText(text)}) Tj ET`);
    };

    const drawWrappedText = (text: string, x: number, y: number, maxChars: number, size = 10, font = 'F1', lineGap = 14) => {
      let currentY = y;
      wrapPdfLine(text, maxChars).forEach((line) => {
        drawText(line, x, currentY, size, font);
        currentY -= lineGap;
      });
      return currentY;
    };

    drawText('A1 - Block A Vasant Kunj Enclave', 54, 686, 10, 'F2');
    drawText('New Delhi -110070', 54, 672, 10, 'F2');

    drawText('Tour Name', 356, 736, 9, 'F2');
    drawWrappedText(tourNameValue, 428, 736, 24, 8.5, 'F2', 10);
    drawText('Date of Issue', 356, 716, 9);
    drawWrappedText(dateOfIssue, 428, 716, 24, 8.5, 'F1', 10);
    drawText('GST no', 356, 696, 9, 'F2');
    drawText('07AAFCI3916H1ZM', 428, 696, 8.5, 'F2');
    drawText('Agent', 356, 682, 9);
    drawWrappedText(agentText, 428, 682, 24, 8.5, 'F1', 10);

    drawText('Hotel Name', 54, 640, 10);
    drawWrappedText(hotelName, 54, 626, 40, 10, 'F2');

    drawText('Group Name:-', 54, 590, 10, 'F2');
    drawWrappedText(`${groupNameValue}${fileCodeValue ? ` (${fileCodeValue})` : ''}`, 135, 590, 44, 10, 'F2');
    drawText('Number of pax', 356, 602, 9);
    drawText(paxText, 428, 602, 9, 'F2');

    let y = 558;
    contentText.split('\n').forEach((line) => {
      if (y < 108) return;
      if (!line.trim()) {
        y -= 14;
        return;
      }
      const isHeading = /^(Pre|Post)\s|\d+(st|nd|rd|th)\sVisit/i.test(line.trim());
      const maxChars = line.startsWith('Please provide') ? 86 : 76;
      y = drawWrappedText(line, 54, y, maxChars, 10, isHeading ? 'F2' : 'F1', 14);
    });

    drawText('Please collect all extra directly', 54, 82, 10, 'F3');
    drawText('Send Bill to', 54, 66, 10, 'F3');
    drawText('A1 - Block A Vasant Kunj Enclave New Delhi -110070', 115, 66, 10, 'F3');

    const stream = commands.join('\n');
    const resources = logo
      ? '<< /Font << /F1 4 0 R /F2 5 0 R /F3 6 0 R >> /XObject << /Im1 8 0 R >> >>'
      : '<< /Font << /F1 4 0 R /F2 5 0 R /F3 6 0 R >> >>';
    const objects = [
      '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj',
      '2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj',
      `3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources ${resources} /Contents 7 0 R >>\nendobj`,
      '4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj',
      '5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>\nendobj',
      '6 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Oblique >>\nendobj',
      `7 0 obj\n<< /Length ${stream.length} >>\nstream\n${stream}\nendstream\nendobj`,
      ...(logo ? [
        `8 0 obj\n<< /Type /XObject /Subtype /Image /Width ${logo.width} /Height ${logo.height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${logo.data.length} >>\nstream\n${logo.data}\nendstream\nendobj`
      ] : [])
    ];

    let pdf = '%PDF-1.4\n';
    const offsets = [0];
    objects.forEach((object) => {
      offsets.push(pdf.length);
      pdf += `${object}\n`;
    });
    const xrefOffset = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n`;
    pdf += '0000000000 65535 f \n';
    offsets.slice(1).forEach((offset) => {
      pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
    });
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;

    return btoa(pdf);
  };

  const attachRequirementPdf = async () => {
    const content = hotelRequirementText.trim();
    if (!content) {
      alert('Please add requirement content before attaching PDF');
      return;
    }

    const baseName = (bookings[0]?.groupName || tourFileData?.fileCode || hotelName || 'hotel-requirement')
      .replace(/[^a-z0-9_-]+/gi, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);

    setRequirementPdfAttachment({
      filename: `${baseName || 'hotel-requirement'}-requirements.pdf`,
      mimeType: 'application/pdf',
      contentBase64: await buildRequirementPdfBase64(content)
    });
  };

  const getNextDayDisplay = (displayDate: string) => {
    const normalized = formatDateForInput(displayDate);
    if (!normalized) return displayDate;
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return displayDate;
    date.setDate(date.getDate() + 1);
    return formatDateForDisplay(date.toISOString().slice(0, 10));
  };

  const resolveExtensions = () => {
    if (Array.isArray(passedExtensions) && passedExtensions.length > 0) {
      return passedExtensions;
    }

    if (!tourFileData) return undefined;

    try {
      const rawExtensions = typeof tourFileData.extensions === 'string'
        ? JSON.parse(tourFileData.extensions)
        : (tourFileData.extensions || []);

      if (!Array.isArray(rawExtensions)) return [];

      const hotelFilter = (selectedHotelName || hotelName || '').trim().toLowerCase();
      if (!hotelFilter) return rawExtensions;

      return rawExtensions.filter((extension: any) => {
        const extHotel = String(extension?.hotelName || extension?.hotel || '').trim().toLowerCase();
        return extHotel === hotelFilter || extHotel.includes(hotelFilter) || hotelFilter.includes(extHotel);
      });
    } catch {
      return [];
    }
  };

  const { extensionRows, preExtensions, postExtensions } = useMemo(() => {
    const rows = resolveExtensions() || [];
    const normalized = rows.map((row: any) => ({
      ...row,
      position: row.position === 'pre' ? 'pre' : 'post'
    }));
    return {
      extensionRows: rows,
      preExtensions: normalized.filter((row: any) => row.position === 'pre'),
      postExtensions: normalized.filter((row: any) => row.position === 'post')
    };
  }, [tourFileData, passedExtensions, selectedHotelName, hotelName]);

  useEffect(() => {
    if (!isOpen || mode !== 'hotels') return;
    setHotelRequirementText(buildHotelRequirementText());
    setRequirementPdfAttachment(null);
  }, [isOpen, mode, hotelName, selectedHotelName, bookings, tourFileData, passedExtensions]);

  const getExtensionDayName = (value: string) => {
    const normalized = formatDateForInput(value);
    if (!normalized) return '';
    const date = new Date(normalized);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { weekday: 'short' });
  };

  const formatMeals = (meals: string[]) =>
    Array.isArray(meals)
      ? meals.map((meal) => String(meal || '').trim().charAt(0).toUpperCase()).filter(Boolean).join('/')
      : '';

  const formatRemarks = (row: ExtensionRow) => [
    row.breakfastRemark ? `B: ${row.breakfastRemark}` : '',
    row.lunchRemark ? `L: ${row.lunchRemark}` : '',
    row.dinnerRemark ? `D: ${row.dinnerRemark}` : ''
  ].filter(Boolean).join(', ');

  const renderExtensionSection = (title: string, rows: ExtensionRow[]) => {
    if (!rows.length) return null;

    const grouped: Record<string, ExtensionRow[]> = {};
    rows.forEach((row) => {
      const key = String(row.passengerName || 'Unnamed Passenger').trim() || 'Unnamed Passenger';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });

    return (
      <div className="mt-6">
        <div className="mb-3 px-2 py-1.5 bg-slate-100 border border-slate-300 rounded">
          <h3 className="font-semibold text-xs text-slate-700 uppercase tracking-wide">{title}</h3>
        </div>
        {Object.entries(grouped).map(([passengerName, groupRows]) => {
          const firstRow = groupRows[0] || ({} as ExtensionRow);
          const roomText = firstRow.roomCategory ? ` ${firstRow.roomCategory}` : '';
          const paxCount = groupRows.length;

          return (
            <div key={`${title}-${passengerName}`} className="mb-6 border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                <h3 className="font-semibold text-sm">
                  Extension - {passengerName} x {paxCount}{roomText}
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100">
                    <tr className="text-gray-700">
                      <th className="px-2 py-2 text-left font-semibold">Passenger Name</th>
                      <th className="px-2 py-2 text-left font-semibold">Date</th>
                      <th className="px-2 py-2 text-center font-semibold">Day</th>
                      <th className="px-2 py-2 text-left font-semibold">Sector</th>
                      <th className="px-2 py-2 text-left font-semibold">Flight/Train</th>
                      <th className="px-2 py-2 text-left font-semibold">Time</th>
                      <th className="px-2 py-2 text-left font-semibold">Status</th>
                      <th className="px-2 py-2 text-left font-semibold">Guide</th>
                      <th className="px-2 py-2 text-left font-semibold">Type</th>
                      <th className="px-2 py-2 text-left font-semibold">City</th>
                      <th className="px-2 py-2 text-left font-semibold">Hotel</th>
                      <th className="px-2 py-2 text-left font-semibold">Status</th>
                      <th className="px-2 py-2 text-left font-semibold">Room</th>
                      <th className="px-2 py-2 text-left font-semibold">Meals</th>
                      <th className="px-2 py-2 text-left font-semibold">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupRows.map((row, rowIdx) => (
                      <tr key={rowIdx} className="border-t border-gray-100">
                        <td className="px-2 py-2 font-medium">{row.passengerName}</td>
                        <td className="px-2 py-2">{row.date}</td>
                        <td className="px-2 py-2 text-center font-medium">{getExtensionDayName(row.date)}</td>
                        <td className="px-2 py-2">{row.sector}</td>
                        <td className="px-2 py-2">{row.flightNumber}</td>
                        <td className="px-2 py-2">{row.time}</td>
                        <td className="px-2 py-2">{row.status}</td>
                        <td className="px-2 py-2">{row.guideName}</td>
                        <td className="px-2 py-2">{row.guideType}</td>
                        <td className="px-2 py-2">{row.city}</td>
                        <td className="px-2 py-2">{row.hotelName}</td>
                        <td className="px-2 py-2">{row.hotelStatus}</td>
                        <td className="px-2 py-2">{row.roomCategory}</td>
                        <td className="px-2 py-2 font-semibold">{formatMeals(row.meals)}</td>
                        <td className="px-2 py-2 text-gray-600">{formatRemarks(row)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleSend = async () => {
    if (!recipientEmail) {
      alert('Please enter hotel email address');
      return;
    }

    if (!isGoogleConnected) {
      alert('Please connect your Google account first');
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('authToken');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const outgoingAttachments = requirementPdfAttachment
        ? [...attachments, requirementPdfAttachment]
        : attachments;
      const response = await fetch(`${API_URL}/hotel-emails/send-to-hotel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          hotelId: hotelId || '',
          hotelName,
          recipientEmail,
          senderEmail,
          subject,
          greeting: message,
          customMessage: '',
          bookings: orderBookingsForMail(bookings).map(b => ({
            ...b,
            checkIn: formatDateForInput(b.checkIn) || b.checkIn,
            checkOut: formatDateForInput(b.checkOut) || b.checkOut
          })),
          closing,
          senderName,
          mode: mode,
          passengerDetails: tourFileData ? (() => {
            try {
              return typeof tourFileData.passengerDetails === 'string'
                ? JSON.parse(tourFileData.passengerDetails)
                : (tourFileData.passengerDetails || []);
            } catch { return []; }
          })() : undefined,
          extensions: resolveExtensions(),
          attachments: outgoingAttachments
        })
      });

      const data = await response.json();
      
      if (data.success) {
        alert('✅ Draft created in Gmail! Opening Gmail...');
        
        // Open Gmail drafts in new tab
        if (data.data?.gmailUrl) {
          window.open(data.data.gmailUrl, '_blank');
        } else {
          window.open('https://mail.google.com/mail/u/0/#drafts', '_blank');
        }
        
        onClose();
      } else {
        alert('Failed to create draft: ' + data.message);
      }
    } catch (error) {
      console.error('Error creating draft:', error);
      alert('Error creating draft email');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Mail className="w-6 h-6 text-violet-600" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {mode === 'vouchers' ? 'Send Voucher Details' : 'Send Booking Request to Hotel'}
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">{hotelName}</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition">
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Google Connection Status */}
            {!isGoogleConnected && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">Google Account Not Connected</p>
                    <p className="text-sm text-amber-700">Connect your Google account to send emails</p>
                  </div>
                </div>
                <Button onClick={connectGoogleAccount} variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-100">
                  Connect Google
                </Button>
              </div>
            )}

            {/* Email Addresses */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">From (Sender Email)</label>
                <Input
                  type="email"
                  value={senderEmail}
                  onChange={(e) => setSenderEmail(e.target.value)}
                  placeholder="operations@immerseindiatours.com"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">To (Hotel Email) *</label>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="reservations@hotel.com"
                  className="w-full"
                />
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full"
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={10}
                className="w-full font-mono text-sm"
              />
            </div>

            {mode === 'hotels' && (
              <div className="border border-gray-200 rounded-lg p-4 bg-white">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Hotel Requirement PDF Content
                  </label>
                  <div className="flex items-center gap-2">
                    {requirementPdfAttachment && (
                      <span className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-1">
                        Attached: {requirementPdfAttachment.filename}
                      </span>
                    )}
                    <Button
                      type="button"
                      onClick={attachRequirementPdf}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Attach PDF
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={hotelRequirementText}
                  onChange={(e) => {
                    setHotelRequirementText(e.target.value);
                    setRequirementPdfAttachment(null);
                  }}
                  rows={8}
                  className="w-full font-mono text-sm"
                  placeholder="Hotel requirement details for attached PDF"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Edit this text if needed, then click Attach PDF to include it with the Gmail draft.
                </p>
              </div>
            )}

            {/* Booking Details / Tour File Table */}
            <div>
              {mode === 'hotels' && hasPassengerAttachments && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-blue-900 mb-2">Passenger screenshot uploaded</p>
                  <div className="space-y-3">
                    {attachments.map((attachment, index) => (
                      <div key={`${attachment.filename}-${index}`} className="rounded border border-blue-200 bg-white p-2 w-full">
                        <img
                          src={`data:${attachment.mimeType};base64,${attachment.contentBase64}`}
                          alt={attachment.filename}
                          className="w-full h-auto max-h-[360px] object-contain rounded"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  {mode === 'vouchers' ? 'Naming/Rooming list:' : 'Booking Details'}
                </label>
                {mode === 'hotels' && (
                  <Button onClick={addNewRow} variant="outline" size="sm" className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Add Row
                  </Button>
                )}
              </div>
              
              {mode === 'vouchers' && tourFileData && hasPassengerAttachments ? (
                <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                  <div className="space-y-3">
                    {attachments.map((attachment, index) => (
                      <div key={`${attachment.filename}-${index}`} className="rounded border border-emerald-200 bg-white p-2 w-full">
                        <img
                          src={`data:${attachment.mimeType};base64,${attachment.contentBase64}`}
                          alt={attachment.filename}
                          className="w-full h-auto max-h-[360px] object-contain rounded"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : mode === 'vouchers' && tourFileData ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
                        <tr>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Room</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Room Type</th>
                          <th className="px-2 py-2 text-left text-xs font-semibold text-black">Passenger</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">DOB</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Age</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Sex</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Nationality</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Passport No.</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Date Of Issue</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Expiry Date</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Place Of Issue</th>
                          <th className="px-2 py-2 text-center text-xs font-semibold text-black">Dietary Requirements</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let passengers: any[] = [];
                          try {
                            passengers = typeof tourFileData.passengerDetails === 'string' 
                              ? JSON.parse(tourFileData.passengerDetails) 
                              : (tourFileData.passengerDetails || []);
                          } catch { passengers = []; }
                          
                          if (passengers.length === 0) {
                            return (
                              <tr>
                                <td colSpan={12} className="px-4 py-6 text-center text-gray-500">
                                  No passenger details found. Please add passenger information in the Tour File.
                                </td>
                              </tr>
                            );
                          }
                          
                          // Group Twin room passengers by room number
                          const twinRoomGroups: { [key: string]: any[] } = {};
                          const nonTwinPassengers: any[] = [];
                          let roomCounter = 1;
                          
                          passengers.forEach((p: any) => {
                            if (p.roomType === 'Twin' && p.roomNumber) {
                              if (!twinRoomGroups[p.roomNumber]) {
                                twinRoomGroups[p.roomNumber] = [];
                              }
                              twinRoomGroups[p.roomNumber].push(p);
                            } else {
                              nonTwinPassengers.push(p);
                            }
                          });
                          
                          const rows: JSX.Element[] = [];
                          
                          // Render Twin room groups
                          Object.keys(twinRoomGroups).sort().forEach((roomNum) => {
                            const roomPassengers = twinRoomGroups[roomNum];
                            if (roomPassengers.length === 2) {
                              // Two passengers sharing - show in one row
                              const p1 = roomPassengers[0];
                              const p2 = roomPassengers[1];
                              rows.push(
                                <tr key={`twin-${roomNum}`} className="border-t border-gray-200 hover:bg-gray-50">
                                  <td className="px-2 py-2 text-center font-medium">{roomCounter}</td>
                                  <td className="px-2 py-2 text-center">Twin</td>
                                  <td className="px-2 py-2">
                                    <div className="pb-1 border-b border-gray-300">{p1.name || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.name || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.dob ? new Date(p1.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.dob ? new Date(p2.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.age || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.age || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.sex || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.sex || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.nationality || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.nationality || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.passportNo || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.passportNo || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.issueDate ? new Date(p1.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.issueDate ? new Date(p2.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.expiryDate ? new Date(p1.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.expiryDate ? new Date(p2.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.placeOfIssue || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.placeOfIssue || ''}</div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="pb-1 border-b border-gray-300">{p1.dietaryRequirements || ''}</div>
                                    <div className="pt-1 text-gray-600">{p2.dietaryRequirements || ''}</div>
                                  </td>
                                </tr>
                              );
                              roomCounter++;
                            } else {
                              // Single passenger in Twin room or more than 2 - show individually
                              roomPassengers.forEach((p: any) => {
                                rows.push(
                                  <tr key={`passenger-${p.id || roomCounter}`} className="border-t border-gray-200 hover:bg-gray-50">
                                    <td className="px-2 py-2 text-center font-medium">{roomCounter}</td>
                                    <td className="px-2 py-2 text-center">{p.roomType || ''}</td>
                                    <td className="px-2 py-2">{p.name || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.dob ? new Date(p.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                    <td className="px-2 py-2 text-center">{p.age || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.sex || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.nationality || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.passportNo || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.issueDate ? new Date(p.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                    <td className="px-2 py-2 text-center">{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                    <td className="px-2 py-2 text-center">{p.placeOfIssue || ''}</td>
                                    <td className="px-2 py-2 text-center">{p.dietaryRequirements || ''}</td>
                                  </tr>
                                );
                                roomCounter++;
                              });
                            }
                          });
                          
                          // Render non-Twin passengers
                          nonTwinPassengers.forEach((p: any) => {
                            rows.push(
                              <tr key={`passenger-${p.id || roomCounter}`} className="border-t border-gray-200 hover:bg-gray-50">
                                <td className="px-2 py-2 text-center font-medium">{roomCounter}</td>
                                <td className="px-2 py-2 text-center">{p.roomType || ''}</td>
                                <td className="px-2 py-2">{p.name || ''}</td>
                                <td className="px-2 py-2 text-center">{p.dob ? new Date(p.dob).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                <td className="px-2 py-2 text-center">{p.age || ''}</td>
                                <td className="px-2 py-2 text-center">{p.sex || ''}</td>
                                <td className="px-2 py-2 text-center">{p.nationality || ''}</td>
                                <td className="px-2 py-2 text-center">{p.passportNo || ''}</td>
                                <td className="px-2 py-2 text-center">{p.issueDate ? new Date(p.issueDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                <td className="px-2 py-2 text-center">{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</td>
                                <td className="px-2 py-2 text-center">{p.placeOfIssue || ''}</td>
                                <td className="px-2 py-2 text-center">{p.dietaryRequirements || ''}</td>
                              </tr>
                            );
                            roomCounter++;
                          });
                          
                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : mode === 'hotels' ? (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-violet-600 to-purple-600 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Group Name</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Check-in</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Check-out</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Night</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Room</th>
                      <th className="px-3 py-2 text-left text-xs font-semibold text-black">Status</th>
                      <th className="px-3 py-2 text-center text-xs font-semibold text-black">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking, index) => (
                      <tr key={index} className={`border-t border-gray-200 ${booking.isEdited ? 'bg-amber-50' : 'bg-white'} hover:bg-gray-50`}>
                        <td className="px-2 py-2">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={booking.groupName}
                            onChange={(e) => handleBookingEdit(index, 'groupName', e.target.value)}
                            placeholder="Group name"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="date"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={formatDateForInput(booking.checkIn)}
                            onChange={(e) => {
                              const nextDisplay = formatDateForDisplay(e.target.value);
                              handleBookingEdit(index, 'checkIn', nextDisplay);
                              handleBookingEdit(index, 'checkOut', getNextDayDisplay(nextDisplay));
                            }}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="date"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={formatDateForInput(booking.checkOut)}
                            onChange={(e) => handleBookingEdit(index, 'checkOut', formatDateForDisplay(e.target.value))}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min="1"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={booking.nights}
                            onChange={(e) => handleBookingEdit(index, 'nights', parseInt(e.target.value) || 1)}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            type="number"
                            min="1"
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={booking.rooms}
                            onChange={(e) => handleBookingEdit(index, 'rooms', parseInt(e.target.value) || 1)}
                          />
                        </td>
                        <td className="px-2 py-2">
                          <input
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                            value={booking.status || ''}
                            onChange={(e) => handleBookingEdit(index, 'status', e.target.value)}
                            placeholder="Status"
                          />
                        </td>
                        <td className="px-2 py-2 text-center">
                          <button
                            onClick={() => removeRow(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded transition"
                            title="Remove row"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              ) : mode === 'vouchers' ? (
                <div className="text-center py-8 text-gray-500">
                  <p>Loading tour file details...</p>
                </div>
              ) : null}
              
              {mode === 'hotels' && (
                <p className="text-xs text-gray-500 mt-2">
                  💡 Rows with yellow background have been edited
                </p>
              )}
            </div>

            {mode === 'hotels' && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">Extensions</label>
                </div>
                {extensionRows.length === 0 ? (
                  <div className="text-sm text-gray-500 border border-gray-200 rounded-lg p-4">
                    No extensions found for this hotel.
                  </div>
                ) : (
                  <div className="space-y-6">
                    {renderExtensionSection('Pre Extensions', preExtensions)}
                    {renderExtensionSection('Post Extensions', postExtensions)}
                  </div>
                )}
              </div>
            )}

            {/* Closing Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Closing Message</label>
              <Textarea
                value={closing}
                onChange={(e) => setClosing(e.target.value)}
                rows={2}
                className="w-full"
              />
            </div>

            {/* Sender Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sender Name</label>
              <Input
                value={senderName}
                onChange={(e) => setSenderName(e.target.value)}
                className="w-full"
                placeholder="Your Name"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {isGoogleConnected ? (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  Google Connected
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                  Not Connected
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <Button onClick={onClose} variant="outline">
                Cancel
              </Button>
              <Button onClick={handleSend} disabled={sending || !isGoogleConnected} className="bg-violet-600 hover:bg-violet-700 disabled:bg-gray-400 font-medium flex items-center gap-2">
                {sending ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Creating Draft...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Create Draft in Gmail</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
